import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, EllipsisVerticalIcon } from "lucide-react";
import { ToolbarProps, View } from "react-big-calendar";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@/components/DatePicker";
import { format, parse } from "date-fns";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface CustomToolbarProps {
  toolbar: ToolbarProps;
  extraRightButtons?: React.ReactNode;
}

export const CalendarCustomToolbar: React.FC<CustomToolbarProps> = ({ toolbar, extraRightButtons }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const goToBack = () => {
    toolbar.onNavigate("PREV");
  };

  const goToNext = () => {
    toolbar.onNavigate("NEXT");
  };

  const goToToday = () => {
    toolbar.onNavigate("TODAY");
  };

  const viewNames = {
    week: t("common.week"),
    month: t("common.month"),
    year: t("common.year"),
    // agenda: t("common.agenda"),
  };

  const handleDateChange = (dateStr?: string) => {
    if (dateStr) {
      const date = parse(dateStr, "dd-MM-yyyy", new Date());
      toolbar.onNavigate("DATE", date);
    }
  };

  const rightButtons = (
    <>
      {Object.entries(viewNames).map(([view, label]) => (
        <Button
          key={view}
          variant={toolbar.view === view ? "default" : "outline"}
          size="sm"
          onClick={() => toolbar.onView(view as View)}
        >
          {label}
        </Button>
      ))}
    </>
  );

  return (
    <div className="flex items-center gap-2 mb-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={goToToday}>
          {t("common.today")}
        </Button>
        <Button variant="outline" size="sm" onClick={goToBack}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={goToNext}>
          <ChevronRight className="h-4 w-4" />
        </Button>
        <DatePicker value={format(toolbar.date, "dd-MM-yyyy")} onChange={handleDateChange} />
        {extraRightButtons}
      </div>
      {isMobile ? (
        <Popover>
          <PopoverTrigger>
            <Button variant="outline" size="sm">
              <EllipsisVerticalIcon />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-4 w-full" side="bottom">
            {rightButtons}
          </PopoverContent>
        </Popover>
      ) : (
        <div className="flex items-center flex-wrap gap-2 ml-auto">{rightButtons}</div>
      )}
    </div>
  );
};
